import { useCallback, useEffect, useMemo, useState } from 'react';

import { MaintenanceDataItem } from '@/generated/GeneratedApiTypes';

/**
 * Flags derived from the current selection. These are the sole input to the Actions dropdown
 * state machine (plus the WFE `availableActions` gate).
 */
export interface RowSelectionDerived {
    selectedCount: number;
    /** Number of distinct vehicles (asset ids) in the selection. */
    selectedVehicleCount: number;
    /** Every selected row has `solved_by_customer === true`. */
    allSolved: boolean;
    /** No selected row has `solved_by_customer === true`. */
    noneSolved: boolean;
    /** Every selected row has an appointment and they all share the same (non-null) appointment id. */
    allHaveSameAppointment: boolean;
    /** No selected row has an appointment. */
    noneHaveAppointment: boolean;
    /** The single selected vehicle's asset id, when exactly one vehicle is selected. */
    selectedAssetId?: string;
    selectedItems: MaintenanceDataItem[];
}

export interface UseRowSelection extends RowSelectionDerived {
    selectedIds: Set<string>;
    isSelected: (id: string) => boolean;
    toggle: (id: string) => void;
    /** Toggles every row on the current page: selects all if any are unselected, else clears. */
    toggleAll: () => void;
    clear: () => void;
    allSelected: boolean;
    someSelected: boolean;
}

const resourceId = (item: MaintenanceDataItem) => item.resource_id;

/**
 * Tracks row selection for the maintenance-data table, keyed by `resource_id`.
 *
 * Selection resets whenever `resetKey` changes (page change / filter change) and defensively drops
 * any selected id that is no longer present in `rows` (e.g. after a refetch).
 */
export const useRowSelection = (rows: MaintenanceDataItem[], resetKey: unknown): UseRowSelection => {
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    // Reset on page/filter change.
    useEffect(() => {
        setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
    }, [resetKey]);

    // Drop ids that fell out of the current rows (e.g. after refetch).
    useEffect(() => {
        setSelectedIds((prev) => {
            if (prev.size === 0) {
                return prev;
            }
            const present = new Set(rows.map(resourceId));
            const next = new Set([...prev].filter((id) => present.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [rows]);

    const toggle = useCallback((id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    const toggleAll = useCallback(() => {
        setSelectedIds((prev) => {
            const pageIds = rows.map(resourceId);
            const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => prev.has(id));
            return allOnPageSelected ? new Set() : new Set(pageIds);
        });
    }, [rows]);

    const clear = useCallback(() => setSelectedIds(new Set()), []);

    const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

    const selectedItems = useMemo(() => rows.filter((row) => selectedIds.has(resourceId(row))), [rows, selectedIds]);

    const derived = useMemo<RowSelectionDerived>(() => {
        const selectedCount = selectedItems.length;
        const assetIds = new Set(selectedItems.map((item) => item.asset.id));
        const appointmentIds = selectedItems.map((item) => item.appointment?.id);

        const allSolved = selectedCount > 0 && selectedItems.every((item) => item.solved_by_customer === true);
        const noneSolved = selectedCount > 0 && selectedItems.every((item) => item.solved_by_customer !== true);
        const noneHaveAppointment = selectedCount > 0 && appointmentIds.every((id) => !id);
        const allHaveSameAppointment =
            selectedCount > 0 && appointmentIds.every((id): id is string => !!id) && new Set(appointmentIds).size === 1;

        return {
            selectedCount,
            selectedVehicleCount: assetIds.size,
            allSolved,
            noneSolved,
            allHaveSameAppointment,
            noneHaveAppointment,
            selectedAssetId: assetIds.size === 1 ? [...assetIds][0] : undefined,
            selectedItems,
        };
    }, [selectedItems]);

    const currentPageIds = rows.map(resourceId);
    const allSelected = currentPageIds.length > 0 && currentPageIds.every((id) => selectedIds.has(id));
    const someSelected = selectedIds.size > 0;

    return {
        selectedIds,
        isSelected,
        toggle,
        toggleAll,
        clear,
        allSelected,
        someSelected,
        ...derived,
    };
};
